import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/context/AuthContext";
import { addPrescription, updatePrescription } from "@/lib/api";
import { PrescriptionDataTransformer, PrescriptionFormData } from "@/types/prescription";

// Create a schema that matches PrescriptionFormData exactly
const schema = z.object({
  id: z.string().optional(),
  patient_id: z.string().min(1, "Patient ID is required"),
  doctor_id: z.string(),
  clinic_id: z.string(),
  date: z.string().min(1, "Date is required"),
  diagnosis: z.string(),
  quick_notes: z.string(),
  advice: z.string(),
  vitals: z.object({
    bp: z.string().optional(),
    pulse: z.string().optional(),
    spo2: z.string().optional(),
    weight: z.string().optional(),
    temp: z.string().optional(),
    edema: z.string().optional(),
    pallor: z.string().optional(),
    icterus: z.string().optional()
  }),
  complaints: z.array(
    z.object({
      complaint: z.string(),
      frequency: z.string().optional(),
      severity: z.string().optional(),
      duration: z.string().optional(),
      date: z.string().optional()
    })
  ),
  investigations: z.array(z.string()),
  medicines: z.array(
    z.object({
      drug_id: z.string().nullable().optional(),
      brand_name: z.string().optional(),
      generic_name: z.string().optional(),
      product: z.string().optional(),
      dosage: z.string().optional(),
      taken_when: z.string().optional(),
      duration: z.string().optional(),
      quantity: z.union([z.string(), z.number()]).optional(),
      notes: z.string().optional()
    })
  ),
  next_visit: z.object({
    date: z.string().optional(),
    time: z.string().optional(),
    notes: z.string().optional()
  }).optional()
}).transform((data) => ({
  ...data,
  complaints: data.complaints?.filter(c => c.complaint?.trim()) || [],
  investigations: data.investigations?.filter(i => i?.trim()) || [],
  medicines: data.medicines?.filter(m => (m.product)?.trim()) || []
}));

// Create a type alias to satisfy the form resolver
type FormDataType = z.infer<typeof schema>;

// Use the type from the types file instead of deriving from schema
interface UsePrescriptionFormOptions {
  mode?: "create" | "edit";
  patientId: string;
  initialData?: Partial<PrescriptionFormData>;
  onSaved?: () => void;
  onCancel?: () => void;
}

export const usePrescriptionForm = ({
  mode = "create",
  patientId,
  initialData,
  onSaved,
  onCancel
}: UsePrescriptionFormOptions) => {
  const { currentUser } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Add ref to track if initial reset has happened
  const hasInitialResetRef = useRef(false);

  // Optimized default values calculation
  const defaultValues = useMemo((): PrescriptionFormData => {
    if (initialData) {
      return PrescriptionDataTransformer.toFormData(initialData as any, patientId);
    }
    
    return PrescriptionDataTransformer.createEmpty(
      patientId, 
      currentUser?.doctor_id || "", 
      currentUser?.clinic_id || ""
    );
  }, [mode, initialData, patientId, currentUser?.doctor_id, currentUser?.clinic_id]);
  
  const form = useForm<PrescriptionFormData>({
    resolver: zodResolver(schema) as any, // Type assertion to resolve mismatch
    defaultValues,
    mode: "onChange"
  });
  
  const { reset, getValues, formState: { errors, isDirty }, watch } = form;

  // Watch all form values to trigger validation
  const watchedValues = watch();

  // Simplified initialization - only reset once with initial data
  useEffect(() => {
    // Only reset if we have initial data and haven't done the initial reset yet
    if (initialData && !hasInitialResetRef.current) {
      const formData = PrescriptionDataTransformer.toFormData(initialData as any, patientId);
      reset(formData);
      setHasChanges(false);
      hasInitialResetRef.current = true;
    } else if (!initialData && !hasInitialResetRef.current) {
      hasInitialResetRef.current = true;
    }
  }, [mode, initialData, patientId, reset]);

  // Track form changes
  useEffect(() => {
    setHasChanges(isDirty);
  }, [isDirty]);

  // Custom validation to check for meaningful content
  const hasValidChanges = useMemo(() => {
    if (!isDirty) {
      return false;
    }
    const values = getValues();
    const hasComplaintsContent = values.complaints?.some(c => typeof c.complaint === 'string' && c.complaint.trim());
    const hasDiagnosis = typeof values.diagnosis === 'string' && values.diagnosis.trim();
    const hasMedicines = values.medicines?.some(m => (m.brand_name || m.product)?.trim());
    const hasInvestigations = values.investigations?.some(i =>
      typeof i === 'string' && i.trim()
    );
    const hasAdvice = typeof values.advice === 'string' && values.advice.trim();
    const hasNotes = typeof values.quick_notes === 'string' && values.quick_notes.trim();
    const hasVitals = Object.values(values.vitals || {}).some(v => v && v !== 'None');

    return hasComplaintsContent || hasDiagnosis || hasMedicines || 
           hasInvestigations || hasAdvice || hasNotes || hasVitals;
  }, [watchedValues, isDirty, getValues]);

  // Submission handler with proper type annotation
  const handleSubmit = useCallback(async (data: PrescriptionFormData) => {
    if (!hasValidChanges && mode === "create") {
      toast({
        title: "No Content",
        description: "Please add some content before saving",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const prescriptionData = PrescriptionDataTransformer.toApiPayload(data, mode === "edit");
      
      if (mode === "edit" && data.id) {
        await updatePrescription({ ...prescriptionData, id: data.id });
        toast({
          title: "Updated Successfully",
          description: "Prescription has been updated"
        });
      } else {
        await addPrescription(prescriptionData);
        toast({
          title: "Saved Successfully", 
          description: "New prescription has been created"
        });
      }
      
      onSaved?.();
    } catch (error) {
      console.error("Error saving prescription:", error);
      toast({
        title: "Error",
        description: "Failed to save prescription. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [hasValidChanges, mode, onSaved]);

  // Cancel handler
  const handleCancel = useCallback(() => {
    if (hasChanges) {
      const confirmed = window.confirm("You have unsaved changes. Are you sure you want to cancel?");
      if (!confirmed) return;
    }
    onCancel?.();
  }, [hasChanges, onCancel]);

  // Form submission wrapper
  const submitForm = useCallback(() => {
    form.handleSubmit(handleSubmit)();
  }, [form, handleSubmit]);

  return {
    // Form instance
    form,
    
    // Form state
    isSubmitting,
    hasChanges,
    hasValidChanges,
    errors,
    isDirty,
    
    // Form actions
    submitForm,
    handleCancel,
    
    // Form data
    watchedValues,
    getValues,
    
    // Meta
    mode,
    patientId
  };
}; 